import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { CheckCircle2, RefreshCw, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { AuthContext } from '@/contexts/AuthContext'
import { useState, useContext, useEffect } from 'react'
import ErrorAlerts from '@/components/common/feedback/ErrorAlerts'
import { fetcher } from '@/lib/api/fetch'

let timeout = null
export default function Organization() {
  const authContext = useContext(AuthContext)
  const [organization, setOrganization] = useState(
    authContext.user.organization,
  )
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)
  const [runningStaleSweep, setRunningStaleSweep] = useState(false)

  useEffect(() => {
    if (authContext.user.organization) {
      setOrganization(authContext.user.organization)
    }
  }, [authContext.user.organization])

  const saveOrg = async () => {
    if (timeout) {
      clearTimeout(timeout)
    }

    const res = await fetcher(
      `/api/admin/organization/${authContext.user.organization.id}`,
      {
        method: 'POST',
        body: JSON.stringify({
          name: organization.name,
        }),
      },
      authContext,
    )

    const data = await res.json()
    if (res.status !== 200) {
      setError(data.message)
      return
    }

    // Assumption freshness window has its own settings endpoint — only call
    // it when the value actually changed.
    const previousFreshness =
      authContext.user.organization?.assumptionSettings?.freshnessDays
    const nextFreshness = organization?.assumptionSettings?.freshnessDays
    if (
      typeof nextFreshness === 'number' &&
      nextFreshness !== previousFreshness
    ) {
      const settingsRes = await fetcher(
        `/api/admin/organization/${authContext.user.organization.id}/settings/assumptions`,
        {
          method: 'PUT',
          body: JSON.stringify({ freshnessDays: nextFreshness }),
        },
        authContext,
      )
      if (settingsRes.status !== 200) {
        const settingsErr = await settingsRes.json()
        setError(settingsErr.error || 'Failed to save assumption settings')
        return
      }
    }

    authContext.setuser(
      {
        ...authContext.user,
        organization: {
          ...authContext.user.organization,
          name: organization.name,
          assumptionSettings: {
            ...authContext.user.organization.assumptionSettings,
            freshnessDays: nextFreshness,
          },
        },
      },
      () => {},
    )

    setSaved(true)
    setTimeout(() => {
      setSaved(false)
    }, 10000)
  }

  const runMarkStaleSweep = async () => {
    setRunningStaleSweep(true)
    try {
      const res = await fetcher(
        '/api/support/assumptions/mark-stale',
        { method: 'POST' },
        authContext,
      )
      const data = await res.json()
      if (res.status !== 200) {
        toast.error(data.error || 'Failed to run mark-stale sweep')
        return
      }
      const s = data.summary || {}
      toast.success(
        `Mark-stale sweep complete — ${s.assumptionsFlipped ?? 0} flipped across ${s.organizationsScanned ?? 0} orgs`,
      )
    } catch (err) {
      toast.error(err.message || 'Failed to run mark-stale sweep')
    } finally {
      setRunningStaleSweep(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="grid gap-4">
        <ErrorAlerts error={error} />
        <div>
          {saved && (
            <Alert className="mb-2 border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
              <CheckCircle2 className="size-4" />
              <AlertDescription>Organization saved</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="organization-name">Organization Name</Label>
            <Input
              id="organization-name"
              value={organization?.name || ''}
              onChange={e =>
                setOrganization({ ...organization, name: e.target.value })
              }
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="assumption-freshness-days">
            Assumption freshness window (days)
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="assumption-freshness-days"
              type="number"
              min={1}
              max={3650}
              className="max-w-[200px]"
              value={organization?.assumptionSettings?.freshnessDays || 180}
              onChange={e =>
                setOrganization({
                  ...organization,
                  assumptionSettings: {
                    ...organization?.assumptionSettings,
                    freshnessDays: Number(e.target.value),
                  },
                })
              }
            />
            {authContext.user?.isAdmin && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={runningStaleSweep}
                      onClick={runMarkStaleSweep}
                      aria-label="Run mark-stale sweep"
                    >
                      {runningStaleSweep ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <RefreshCw />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Run mark-stale sweep now (all orgs)
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Operating assumptions that haven&apos;t been validated against an
            AAR within this window are automatically marked as stale, which
            reduces the service&apos;s readiness score until they are
            re-validated. Default is 180 days.
          </p>
        </div>
        <div>
          <Button onClick={saveOrg}>Save</Button>
        </div>
      </div>
    </div>
  )
}
